import { createRouter, createWebHistory } from "vue-router";
import Dashboard from "@/views/Dashboard.vue";
import Welcome from "@/views/Welcome.vue";
import Tables from "@/views/Tables.vue";
import Billing from "@/views/Billing.vue";
import VirtualReality from "@/views/VirtualReality.vue";
import ProfileTemplate from "../views/ProfileTemplate.vue";
import Rtl from "@/views/Rtl.vue";
import SignIn from "@/views/SignIn.vue";
import SignUp from "@/views/SignUp.vue";
import Login from "@/views/Login.vue";
import Register from "@/views/Register.vue";
import SendEmail from "../views/SendEmail.vue";
import ResetPassword from "../views/ResetPassword.vue";
import Profile from "@/views/Profile.vue";
import Users from "../views/Users.vue";
import store from "/src/store";
import guest from "../middlewares/guest.js";
import auth from "../middlewares/auth.js";
import axios from "axios";

function middlewarePipeline(context, middleware, index) {
  const nextMiddleware = middleware[index];

  if (!nextMiddleware) {
    return context.next;
  }

  return () => {
    const nextPipeline = middlewarePipeline(context, middleware, index + 1);

    nextMiddleware({ ...context, next: nextPipeline });
  };
}

// Middleware para verificar permisos de módulo
const checkModulePermission = (requiredModule) => {
  return ({ next, store }) => {
    // Obtener permisos del usuario desde el store
    const userPermissions = store.state.user?.permissions || {};
    
    // Verificar si el módulo requiere un permiso específico
    if (!requiredModule) {
      return next();
    }
    
    // Verificar si el usuario tiene permiso para el módulo requerido
    const hasPermission = userPermissions[requiredModule] === true;
    
    if (hasPermission) {
      return next();
    } else {
      console.warn(`Acceso denegado al módulo: ${requiredModule}. Permisos disponibles:`, userPermissions);
      // Redirigir a página de acceso denegado
      return next({ name: 'AccessDenied' });
    }
  };
};

const routes = [
  {
    path: "/",
    name: "/",
    redirect: "/welcome",
  },
  {
    path: "/welcome",
    name: "Welcome",
    component: Welcome,
    meta: {
      requiresAuth: true,
      middleware: [auth],
      isWelcome: true
    }
  },
  {
    path: "/dashboard",
    name: "Dashboard",
    component: Dashboard,
    meta: {
      requiresAuth: true,
      middleware: [auth, checkModulePermission('dashboard')],
      requiredModule: 'dashboard'
    }
  },
  {
    path: "/tables",
    name: "Tables",
    component: Tables,
    meta: {
      requiresAuth: true,
      middleware: [auth, checkModulePermission('admin')],
      requiredModule: 'admin'
    }
  },
  {
    path: "/billing",
    name: "Billing",
    component: Billing,
    meta: {
      requiresAuth: true,
      middleware: [auth, checkModulePermission('bulk_loads')],
      requiredModule: 'bulk_loads'
    }
  },
  {
    path: "/virtual-reality",
    name: "Virtual Reality",
    component: VirtualReality,
    meta: {
      requiresAuth: true,
      middleware: [auth, checkModulePermission('admin')],
      requiredModule: 'admin'
    }
  },
  {
    path: "/profileTemplate",
    name: "ProfileTemplate",
    component: ProfileTemplate,
    meta: {
      requiresAuth: true,
      middleware: [auth, checkModulePermission('dashboard')],
      requiredModule: 'dashboard'
    }
  },
  {
    path: "/rtl-page",
    name: "Rtl",
    component: Rtl,
    meta: {
      requiresAuth: true,
      middleware: [auth, checkModulePermission('admin')],
      requiredModule: 'admin'
    }
  },
  {
    path: "/actualizacion-masiva",
    name: "ActualizacionMasiva",
    component: () => import("@/views/ActualizacionMasiva.vue"),
    meta: {
      requiresAuth: true,
      middleware: [auth, checkModulePermission('bulk_update')],
      requiredModule: 'bulk_update'
    }
  },
  {
    path: "/sign-in",
    name: "Sign In",
    component: SignIn,
    meta: {
      middleware: [guest]
    }
  },
  {
    path: "/sign-up",
    name: "Sign Up",
    component: SignUp,
    meta: {
      middleware: [guest]
    }
  },
  {
    path: "/login",
    name: "Login",
    component: Login,
    meta: {
      middleware: [guest]
    }
  },
  {
    path: "/register",
    name: "Register",
    component: Register,
    meta: {
      middleware: [guest]
    }
  },
  {
    path: "/password/email",
    name: "SendEmail",
    component: SendEmail,
    meta: {
      middleware: [guest]
    }
  },
  {
    path: "/password/reset",
    name: "ResetPassword",
    component: ResetPassword,
    meta: {
      middleware: [guest]
    }
  },
  {
    path: "/profile",
    name: "Profile",
    component: Profile,
    meta: {
      requiresAuth: true,
      middleware: [auth, checkModulePermission('dashboard')],
      requiredModule: 'dashboard'
    }
  },
  // Nueva ruta para Cargar Export (antes de Activity)
  {
    path: "/cargar-export",
    name: "CargarExport",
    component: () => import("@/views/CargarExport.vue"),
    meta: {
      requiresAuth: true,
      middleware: [auth, checkModulePermission('cargar_export')],
      requiredModule: 'cargar_export'
    }
  },
  // Nueva ruta para Actividad (antes de Users)
  {
    path: "/activity",
    name: "Activity",
    component: () => import("@/views/Activity.vue"),
    meta: {
      requiresAuth: true,
      middleware: [auth, checkModulePermission('activity')],
      requiredModule: 'activity'
    }
  },
  {
    path: "/users",
    name: "Users",
    component: Users,
    meta: {
      requiresAuth: true,
      middleware: [auth, checkModulePermission('users')],
      requiredModule: 'users'
    }
  },
  {
    path: "/localizaciones",
    name: "Localizaciones",
    component: () => import("@/views/Localizaciones.vue"),
    meta: {
      requiresAuth: true,
      middleware: [auth, checkModulePermission('locations')],
      requiredModule: 'locations'
    }
  },  
  // Nueva ruta para RegistroBorrado
  {
    path: "/RegistroBorrado",
    name: "BorradoRegistros",
    component: () => import("@/views/BorradoRegistros.vue"),
    meta: {
      requiresAuth: true,
      middleware: [auth, checkModulePermission('delete_records')],
      requiredModule: 'delete_records'
    }
  },
  // Nuevas rutas para CelSig5 y Conectividad
  {
    path: "/celsig5",
    name: "CelSig5",
    component: () => import("@/views/CelsigScegr.vue"),
    meta: {
      requiresAuth: true,
      middleware: [auth, checkModulePermission('celsig5')],
      requiredModule: 'celsig5'
    }
  },
  {
    path: "/conectividad",
    name: "Conectividad",
    component: () => import("@/views/Conectividad.vue"),
    meta: {
      requiresAuth: true,
      middleware: [auth, checkModulePermission('connectivity')],
      requiredModule: 'connectivity'
    }
  },
  // Página de acceso denegado
  {
    path: "/access-denied",
    name: "AccessDenied",
    component: () => import("@/views/AccessDenied.vue"),
    meta: {
      requiresAuth: true,
      middleware: [auth]
    }
  }
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  linkActiveClass: "active",
});

// Middleware global para verificar permisos cuando cambia la ruta
router.beforeEach(async (to, from, next) => {
  const context = {
    to,
    from,
    next,
    store,
  };

  // Si no tiene configuración de middleware, aplicar verificación de autenticación
  if (!to.meta.middleware) {
    return auth({ ...context, next: next });
  }

  // Verificar si necesitamos cargar los permisos del usuario
  if (to.meta.requiresAuth && 
      (!store.state.user || !store.state.user.permissions || Object.keys(store.state.user.permissions).length === 0)) {
    try {
      // Intentar obtener permisos del usuario si el store no los tiene
      const token = localStorage.getItem('auth_token');
      if (token) {
        const apiUrl = process.env.VUE_APP_API_BASE_URL || 'http://teleconnect.test/api/v2';
        
        // Primero, obtener datos del usuario
        const userResponse = await axios.get(`${apiUrl}/me`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          }
        });
        
        if (userResponse.data && userResponse.data.data) {
          // Guardar usuario en el store
          store.commit('setUser', userResponse.data.data);
        }
        
        // Luego, obtener permisos específicamente
        const permissionsResponse = await axios.get(`${apiUrl}/me/permissions`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          }
        });
        
        if (permissionsResponse.data && permissionsResponse.data.success && permissionsResponse.data.data) {
          // Guardar permisos en el store
          store.commit('setUserPermissions', permissionsResponse.data.data);
          console.log("Permisos cargados en el router:", permissionsResponse.data.data);
        } else {
          console.warn("No se pudieron cargar los permisos: formato inválido");
        }
      } else {
        console.warn("No hay token de autenticación disponible");
      }
    } catch (error) {
      console.error('Error al obtener información del usuario:', error);
    }
  }

  // Verificar si el usuario intenta acceder a una ruta para la cual no tiene permiso
  if (to.meta.requiredModule) {
    const userPermissions = store.state.user?.permissions || {};
    const requiredModule = to.meta.requiredModule;
    
    // Si no tiene el permiso específico y no es la página de acceso denegado o bienvenida, redirigir
    if (!userPermissions[requiredModule] && to.name !== 'AccessDenied' && to.name !== 'Welcome') {
      console.warn(`Redirigiendo: usuario sin permiso para módulo ${requiredModule}`);
      
      // Si el usuario no tiene permisos para ningún módulo, ir a welcome
      const hasAnyPermission = Object.values(userPermissions).some(permission => permission === true);
      if (!hasAnyPermission) {
        return next({ name: 'Welcome' });
      }
      
      return next({ name: 'AccessDenied' });
    }
  }

  // Aplicar pipeline de middleware
  const middleware = to.meta.middleware;
  return middleware[0]({
    ...context,
    next: middlewarePipeline(context, middleware, 1),
  });
});

export default router;